import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Loader from 'src/components/Loader';
import { Alert, Box, Typography } from '@mui/material';
import { InformationWidgetFieldTypes } from 'src/components/widgets/information-widget';
import StorageUnitTable from 'src/components/common/StorageUnitTable';
import { DataService } from 'src/services/data-service';
import AddIcon from '@mui/icons-material/Add';
import { ITableMenuAction } from 'src/components/enhanced-table';
import ModernInformationDisplay from 'src/components/widgets/information-widget/modernInformationDisplay';
import CreateStorageUnitDrawer from '../StorageUnit/CreateStorageUnitDrawer';
import type { StorageUnit, StorageUnitDetail, StorageUnitDetailTableRow } from 'src/types/StorageUnit.types';
import UpdateStorageUnitDrawer from '../StorageUnit/UpdateStorageUnitDrawer';
import StatusCard from 'src/components/StatusCard';

interface WarehouseDetailData {
  id: string;
  code: string;
  name: string;
  virtual: boolean;
  location: string;
  dimentsions: string;
  firstLevelStorageUnits: StorageUnitDetailTableRow[];
}

const WarehouseDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<WarehouseDetailData | null>(null);
  const nav = useNavigate();
  const [openAdd, setOpenAdd] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [updateData, setUpdateData] = useState<StorageUnitDetail | undefined>(undefined);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);


  useEffect(() => {
    const fetchDetail = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await DataService.get(`api/Warehouse/${id}`, {});
        const json = await res.json();
        if (json.success && json.data) {
          setData(json.data);
        } else {
          setNotFound(true);
          setLoading(false);
          return;
        }
      } catch (e) {
        setError('Failed to load warehouse details');
      }
      setLoading(false);
    };
    if (id) fetchDetail();
  }, [id]);

  if (loading) return <Loader/>;
  if (notFound)
   return (
     <StatusCard
       layout="page"
       variant="not-found"
       imageSrc={process.env.PUBLIC_URL + '/static/images/status/error1.svg'}
       title="Warehouse Not Found"
       message="Sorry, this warehouse doesn’t exist. Check the code or go back."
       actionLabel="Back to List"
       onAction={() => nav('/warehouse')}
     />
   );

  if (!data) return null;

  const fields = [
    { name: 'code', title: 'Code', type: InformationWidgetFieldTypes.Text, width: 'third' as const },
    { name: 'name', title: 'Name', type: InformationWidgetFieldTypes.Text, width: 'third' as const },
    { name: 'virtual', title: 'Virtual', type: InformationWidgetFieldTypes.Boolean, width: 'third' as const },
    { name: 'location', title: 'Location', type: InformationWidgetFieldTypes.Text, width: 'third' as const },
    { name: 'dimensions', title: 'Dimensions', type: InformationWidgetFieldTypes.Text, width: 'third' as const },
  ];

  const toStorageUnit = (su: StorageUnitDetailTableRow): StorageUnitDetail => ({
    id: su.id,
    code: su.code,
    name: su.name,
    warehouseId: '',
    parentStorageUnitId: undefined,
    level: su.level,
    isLeaf: su.isLeaf,
    parentWarehouseName: '',
    storageUnits: [],
    childUnits: [],
    parentStorageUnitName: su.parentStorageUnitName || null,
    storageItems: [],
    storageTypeId: '',
    isActive: true,
    properties: {},
    hasProperties: false,
    propertyCount: 0,
    isAvailableForStorage: true,
    createdAt: '',
    updatedAt: '',
    aggregatedUniqueProducts: 0,
    aggregatedTotalQuantity: 0
  });

  const tableData: Record<string, StorageUnitDetailTableRow> = Object.fromEntries(
    (data.firstLevelStorageUnits || []).map((su) => [
      su.id,
      {
        id: su.id,
        code: su.code,
        name: su.name,
        warehouseName: su.warehouseName,
        parentStorageUnitName: su.parentStorageUnitName || '-',
        level: su.level,
        isLeaf: su.isLeaf,
        UniqueProducts: (su as any).aggregatedUniqueProducts ?? (su as any).uniqueProducts ?? 0,
        TotalQuantity: (su as any).aggregatedTotalQuantity ?? (su as any).totalQuantity ?? 0,
      }
    ])
  );

  const actions: ITableMenuAction[] = [
    {
      key: 'addStorageUnit',
      title: 'Add Storage Unit',
      icon: <AddIcon />,
      onClick: () => setOpenAdd(true),
      visible: () => true,
      disabled: () => false,
    }
  ];

  const handleEdit = (id: string) => {
    const found = (data.firstLevelStorageUnits || []).find(su => su.id === id);
    setUpdateData(found ? toStorageUnit(found) : undefined);
    setOpenEdit(true);
  };

  const handleDelete = (id: string) => {
    setDeleteId(id);
  };
  const storageUnitsForDrawer: StorageUnitDetail[] = (data?.firstLevelStorageUnits || []).map(toStorageUnit);

  return (
    <Box sx={{ mx: 'auto' }}>
      <ModernInformationDisplay
        fields={fields}
        data={data}
        variant='property-grid'
      />
      <Box mt={5}>
        <StorageUnitTable
          data={tableData}
          loading={loading}
          actions={actions}
          onEdit={handleEdit}
          onDelete={handleDelete}
          visibleColumns={['code', 'name', 'UniqueProducts', 'TotalQuantity','actions'] as any}
        />
      </Box>
      {openAdd && (
        <CreateStorageUnitDrawer
          open={openAdd}
          onClose={() => { setOpenAdd(false); }}
          onSuccess={() => { setOpenAdd(false); window.location.reload(); }}
          storageUnits={storageUnitsForDrawer}
          defaultWarehouseId={data?.id}
          infoWarehouse={data ? { Code: data.code, Name: data.name } : undefined}
          infoParentStorageUnit={{ Code: '', Name: '' }}
          forceParentWarehouseId={data?.id}
        />
      )}
      {openEdit && updateData && (
        <UpdateStorageUnitDrawer
          open={openEdit}
          onClose={() => { setOpenEdit(false); setUpdateData(undefined); }}
          onSuccess={() => { setOpenEdit(false); setUpdateData(undefined); window.location.reload(); }}
          updateData={updateData}
          warehouses={data ? [{ Id: data.id, Name: data.name }] : []}
          storageUnits={storageUnitsForDrawer}
          infoWarehouse={data ? { Code: data.code, Name: data.name } : undefined}
          infoParentStorageUnit={{ Code: '', Name: '' }}
          forceParentWarehouseId={data?.id}
        />
      )}
    </Box>
  );
};

export default WarehouseDetail; 